import apiFactory from "@/helpers/api";
import OutcomeType from "@/constants/OutcomeType";
import OutcomeSortType from "@/constants/OutcomeSortType";
import OutcomeStatus from "@/constants/OutcomeStatusV2";
import TeamMemberStatusType from "@/constants/TeamMemberStatusType";
import TeamType from "@/constants/TeamType";
import TeamRoleType from "@/constants/TeamRoleType";
import CommitmentFilterType from "@/constants/CommitmentFilterType";

const {
	get: apiGet,
	del: apiDel,
	post: apiPost,
	put: apiPut,
} = apiFactory();

export const fetchTeam = async ( { teamId, } ) => {
	return apiGet( `/team/${teamId}` );
};

export const createTeam = async team => {
	return await apiPost( "/team", team );
};

export const createOneOnOne = async ( { managerEmail, managerName, isInitiatorLead, reportEmail, reportRole, relatedTeamId, } ) => {
	let initiatorRole = reportRole === TeamRoleType.LEAD ? TeamRoleType.PEER : TeamRoleType.MANAGER;
	let teamMemberRole = reportRole === TeamRoleType.LEAD ? TeamRoleType.PEER : TeamRoleType.REPORT;

	if ( !isInitiatorLead ) {
		initiatorRole = TeamRoleType.PEER;
		teamMemberRole = TeamRoleType.PEER;
	}
	const team = {
		name: `${ managerName } : ${ reportEmail }`,
		type: TeamType.ONE_ON_ONE,
		members: [
			{
				email: managerEmail,
				role: initiatorRole,
				status: TeamMemberStatusType.ACTIVE,
			},
			{
				email: reportEmail,
				role: teamMemberRole,
				status: TeamMemberStatusType.INVITED,
			},
		],
		relatedTeamId,
	};
	return createTeam( team );
};

export const saveTeam = async ( { teamId, name, } ) => {
	return apiPost( `/team/${teamId}`, { name, } );
};

export const removeTeamMember = async ( { teamId, personId, } ) => {
	return apiDel( `/team/${teamId}/member/${personId}` );
};

export const inviteTeamMember = async ( { teamId, email, } ) => {
	return apiPost( `/team/${teamId}/member`, { email, status: TeamMemberStatusType.INVITED, } );
};

export const updateTeamMember = async ( { teamId, personId, role, } ) => {
	await apiPost( `/team/${teamId}/member/${personId}`, { role, } );
};

/**
 * @param {String} teamId
 * @param {Object} options
 * @param {OutcomeStatus} options.status = OutcomeStatus.ALL
 * @param {OutcomeSortType} options.sortType = OutcomeSortType.OUTCOME_IMPORTANCE
 * @param {CommitmentFilterType} options.filterType = CommitmentFilterType.OUTSTANDING
 * @param {String} [options.completedAfter] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.completedBefore] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.dueAfter] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.dueBefore] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.createdBefore] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {Number} [options.offset] = 0
 * @param {Number} [options.limit] = 10
 */
export const fetchCommitments = async ( teamId, options ) => {
	const url = "/outcome";

	const defaultOptions = {
		status: OutcomeStatus.ALL,
		sortType: OutcomeSortType.OUTCOME_IMPORTANCE,
		filterType: CommitmentFilterType.OUTSTANDING,
		offset: 0,
		limit: 10,
	};

	const unyieldingOptions = {
		outcomeType: OutcomeType.COMMITMENT,
	};

	const params = Object.assign( {
		teamId,
	}, defaultOptions, options, unyieldingOptions );

	return apiGet( url, params );
};

export const fetchCommitmentMetadata = ( { teamId, } ) => {
	const url = `/team/${ teamId }/commitment-metadata`;
	return apiGet( url );
};

export const updateCommitment = ( { commitment, meetingId, }, options = { version: 2, } ) => {
	const url = `/outcome/${ commitment.id}`;
	const headers = {
		"x-api-version": options.version,
	};
	return apiPut( url, { ...commitment, meetingId, }, headers );
};

/**
 * @param {String} teamId
 * @param {Object} options
 * @param {String} [options.completedAfter] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.completedBefore] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.dueAfter] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.dueBefore] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {String} [options.createdAfter] = "" (YYYY-MM-DD; will not be sent if empty)
 * @param {Number} [options.offset] = 0
 * @param {Number} [options.limit] = 10
 */
export const fetchRecentDecisions = async ( teamId, options ) => {
	const url = "/outcome";

	const unyieldingOptions = {
		status: OutcomeStatus.ALL,
		sortType: OutcomeSortType.CREATED_RECENTLY,
		outcomeType: OutcomeType.DECISION,
		includeMeetings: 1,
	};

	const params = Object.assign( {
		teamId,
	}, options, unyieldingOptions );

	const headers = {
		"x-api-version": 3,
	};

	delete params.filterType;
	delete params.status;

	return apiGet( url, params, headers );
};

export const fetchTeamMemberDemographics = async ( { personId, teamId, } ) => {
	return apiGet( `/team/${ teamId }/member/${ personId }/demographics` );
};

export const favoriteTeam = async ( { teamId, } ) => {
	const url = `/team/${teamId}/favorite`;
	return apiPost( url, {}, {} );
};

export const unfavoriteTeam = async ( { teamId, } ) => {
	const url = `/team/${teamId}/favorite`;
	return apiDel( url, {}, {} );
};



// WEBPACK FOOTER //
// ./src/api/teamAPI.js